import React from "react";

import type { AuditEntry } from "../api";
import { auditCategory, auditLabel, dotColor } from "../logic";

function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Renders ops audit entries. `compact` = the inline recent-audit list on a
 * detail pane; otherwise the full Audit-log page row. */
export default function AuditList({
  entries,
  compact = false,
  emptyText = "No audit entries.",
}: {
  entries: AuditEntry[];
  compact?: boolean;
  emptyText?: string;
}) {
  if (!entries.length) {
    return <p style={{ color: "var(--text-muted)", fontSize: 13 }}>{emptyText}</p>;
  }

  if (compact) {
    return (
      <>
        {entries.map((e) => (
          <div key={e.id} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: "1px solid var(--border-hairline)", fontSize: 12.5 }}>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-faint)", whiteSpace: "nowrap" }}>{fmtTs(e.created_at)}</span>
            <div>
              <span style={{ color: "var(--ink-900)", fontWeight: 600 }}>{auditLabel(e.action, e.after)}</span>
              {e.reason && <span style={{ color: "var(--text-muted)" }}> — {e.reason}</span>}
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {entries.map((e) => (
        <div key={e.id} style={{ display: "flex", gap: 16, padding: "15px 22px", borderBottom: "1px solid var(--border-hairline)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 6, flex: "none", background: dotColor(auditCategory(e.action)) }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-900)" }}>{auditLabel(e.action, e.after)}</span>
              {e.target_label && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)" }}>{e.target_label}</span>}
            </div>
            {e.reason && <div style={{ fontSize: 12.5, color: "var(--text-body)", marginTop: 3 }}>“{e.reason}”</div>}
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
              {fmtTs(e.created_at)} · {e.actor_name || e.actor_user_id || "system"}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
